import { IconName, KebabMenuOption } from '@focaldata/cin-ui-components'
import { useState } from 'react'
import { isMultilangPocEnabled } from '../../../../constants/featureFlags'
import useDownloadSurveyDoc from '../../../../hooks/useDownloadSurveyDoc'
import { useProjectType } from '../../../../modules/Project/Project.hooks'
import ExportSurveyDialog from './ExportSurveyDialog'
import { useTranslatedLanguageOptions } from './exportLanguageOptions'

/**
 * The survey-export items and their dialog, shared by every kebab that offers
 * them so the labels, icons and behaviour stay identical. Each surface renders
 * its own KebabMenu: the questionnaire subheader needs an offset and a muted
 * icon, the results bars use the design-system default.
 *
 * Must be called under a resolvable surveyId (route param or SurveyIdContext) —
 * the export hooks read one.
 */
export const useSurveyExportMenu = (
  defaultLanguage?: string,
  initialMarketSurveyId?: string
): {
  exportOptions: KebabMenuOption[]
  exportDialog: JSX.Element | null
} => {
  const { exportSurvey } = useDownloadSurveyDoc()
  const [byLanguageOpen, setByLanguageOpen] = useState(false)
  const {
    isMultiMarketV2,
    hasMarkets,
    loading: projectTypeLoading
  } = useProjectType()
  const multiMarket = isMultiMarketV2 || Boolean(initialMarketSurveyId)
  const enabled = isMultilangPocEnabled()
  // Use the same stored-translation source as the language picker, not just
  // fieldwork allocations (which can have no translations to export).
  const languages = useTranslatedLanguageOptions(enabled && !multiMarket)
  const useDialog =
    enabled &&
    (byLanguageOpen ||
      multiMarket ||
      languages.loading ||
      languages.failed ||
      languages.options.length > 1)

  const exportOptions: KebabMenuOption[] = [
    {
      id: 0,
      textItem: useDialog ? 'Export survey…' : 'Export survey (.docx)',
      onClickItem: useDialog
        ? () => setByLanguageOpen(true)
        : () =>
            initialMarketSurveyId
              ? exportSurvey(undefined, { includeAudienceQuestions: false })
              : exportSurvey(),
      iconName: IconName.Export
    }
  ]

  return {
    exportOptions,
    exportDialog: useDialog ? (
      <ExportSurveyDialog
        open={byLanguageOpen}
        onClose={() => setByLanguageOpen(false)}
        defaultLanguage={defaultLanguage}
        multiMarket={multiMarket}
        sharedDocsIncludeScreeners={!(hasMarkets || multiMarket)}
        projectTypeLoading={projectTypeLoading}
        initialMarketSurveyId={initialMarketSurveyId}
      />
    ) : null
  }
}
